import { Injectable } from '@angular/core';
// eslint-disable-next-line import/no-cycle
import { MenuType } from '../../../app.menu';
import { ProjectProfile } from '../../../models/projects.model';
import { UserIs, UserIsAndHasTag } from '../../../models/user-is.model';
// eslint-disable-next-line import/no-cycle
import { AuthorizationService } from '../../authorization.service';
import { accessRulesConfig } from '../configs/access-rules.config';
import { AccessRulesStateService } from './access-rules-state.service';

@Injectable({
  providedIn: 'root',
})
export class AccessRulesValidationService {
  constructor(
    private stateService: AccessRulesStateService,
    private authorizationSvc: AuthorizationService,
  ) {}

  getAccessRulesValue() {
    return accessRulesConfig;
  }

  getAccessMenuByFeature() {
    const features = this.stateService.getFeatures();
    return {
      [MenuType.CLIENT_REPORTS]: features.clientManagement.enable,
      [MenuType.CONTRACTS_AND_RENEWALS]: features.clientManagement.enable,
      [MenuType.INVOICES]: features.clientManagement.enable,
      [MenuType.STATEMENTS]: features.clientManagement.enable,
      [MenuType.PAYMENTS]: features.clientManagement.enable,
      [MenuType.BILLING]: features.clientManagement.enable,
      [MenuType.BILLING_REPORTS]: features.clientManagement.enable,
      [MenuType.TOOLS]:
        features.plextera.integrations.sso.enable || features.fax.documentFaxEnabled,
      [MenuType.TOOLS_PLEXTERA]: features.plextera.integrations.sso.enable,
      [MenuType.ABOUT]: features.aboutPage.enable,
      [MenuType.RATE_NOTICES]: features.rateNotices.enable,
      [MenuType.STATEMENTS_OF_ACCOUNT]: features.statementsOfAccount.enable,
      [MenuType.SIDES]: features.sides.enable,
      [MenuType.ACTIVITIES]:
        features.rateNotices.enable ||
        (features.benefitCharges.enable &&
          features.partnerProgram.enable &&
          features.clientManagement.enable) ||
        features.mailingLabels.enable,
      [MenuType.ACTIVITIES_RATE_NOTICES]: features.rateNotices.enable,
      [MenuType.ACTIVITIES_BENEFIT_CHARGES]:
        features.benefitCharges.enable &&
        features.partnerProgram.enable &&
        features.clientManagement.enable,
      [MenuType.ACTIVITIES_PARTNER_PROGRAM_MONTHLY_EMAIL]:
        features.benefitCharges.enable &&
        features.partnerProgram.enable &&
        features.clientManagement.enable,
      [MenuType.ACTIVITIES_MAILING_LABELS]: features.mailingLabels.enable,
      [MenuType.REPORTS]:
        this.isBenefitChargesReportVisible() ||
        features.benefitCharges.reportVisibility.annualPages ||
        features.benefitCharges.reportVisibility.uiActivityReport ||
        (features.rateNotices.enable && !this.getAccessRulesValue().menu[MenuType.RATE_NOTICES]) ||
        (features.partnerProgram.enable &&
          !this.getAccessRulesValue().menu[MenuType.REPORTS_PARTNER_PROGRAMS]),
      [MenuType.REPORTS_BENEFIT_CHARGES]:
        this.isBenefitChargesReportVisible() &&
        this.getAccessRulesValue().menu[MenuType.REPORTS_BENEFIT_CHARGES],
      [MenuType.REPORTS_UI_ACTIVITY_REPORT]:
        features.benefitCharges.reportVisibility.uiActivityReport,
      [MenuType.REPORTS_PARTNER_PROGRAMS]: features.partnerProgram.enable,
      [MenuType.REPORTS_RATE_NOTICES]: features.rateNotices.enable,
      [MenuType.REPORTS_ANNUAL_PAGES]: features.benefitCharges.reportVisibility.annualPages,
      [MenuType.PROTESTS]: features.protest.enable,
      [MenuType.ANALYTICS]: features.analystPerformance.enable,
      [MenuType.ANALYTICS_ANALYST_PERFORMANCE]: features.analystPerformance.enable,
      [MenuType.SETTINGS_PARTNER_PROGRAMS]: features.partnerProgram.enable,
      [MenuType.SETTINGS_DATA]:
        features.reasonCodes.enable ||
        features.hearings.enable ||
        features.states.enable ||
        features.managedRules.enable,
      [MenuType.SETTINGS_DATA_REASON_CODES]: features.reasonCodes.enable,
      [MenuType.SETTINGS_DATA_HEARINGS]: features.hearings.enable,
      [MenuType.SETTINGS_DATA_STATES]: features.states.enable,
      [MenuType.SETTINGS_DATA_MANAGED_RULES]: features.managedRules.enable,
      [MenuType.SETTINGS_SECURITY]:
        features.twoFactorAuthentication?.pin?.enable ||
        features.twoFactorAuthentication?.totp?.enable,
      [MenuType.TOOLS_FAX_CENTER]: features.fax.documentFaxEnabled,
      [MenuType.TOOLS_LETTER_TEMPLATES]: features.letterTemplate.enable,
    };
  }

  isFeatureEnabled(enableValue: boolean | undefined): boolean {
    return enableValue !== undefined ? enableValue : true;
  }

  isBenefitChargesReportVisible() {
    const features = this.stateService.getFeatures();
    if (!this.authorizationSvc.userIs([UserIs.INTERNAL])) {
      return features.benefitCharges.reportVisibility.chargeHistoryReport;
    }

    return (
      features.benefitCharges.reportVisibility.chargeHistoryReport ||
      features.benefitCharges.reportVisibility.reimbursableBillsList ||
      features.benefitCharges.reportVisibility.fraudulentClaimCharges ||
      features.benefitCharges.reportVisibility.reimbursableBillsCopies ||
      features.benefitCharges.reportVisibility.covidChargesAndCredits ||
      features.benefitCharges.reportVisibility.protestableBCPerStateProgram ||
      features.benefitCharges.reportVisibility.protestFactFindingTimeliness ||
      features.benefitCharges.reportVisibility.protestableBenefitChargesReport ||
      features.benefitCharges.reportVisibility.missingBenefitChargeStatementsReport ||
      features.benefitCharges.reportVisibility.remainingLiability
    );
  }

  isAllowedByProfileRule(rule: any): boolean {
    const profile = this.stateService.getProfile();

    // If rule is not provided or is null/undefined, allow access
    if (rule === null || rule === undefined) {
      return true;
    }

    // Handle boolean values (true = always allow, false = always deny)
    if (typeof rule === 'boolean') {
      return rule;
    }

    if (Array.isArray(rule)) {
      // Check if it's an array of ProjectProfiles (old format)
      if (rule.length > 0 && Object.values(ProjectProfile).includes(rule[0] as ProjectProfile)) {
        return rule.includes(profile);
      }

      // If it's an array of roles with tags (new common format)
      if (
        rule.length > 0 &&
        typeof rule[0] === 'object' &&
        'roles' in rule[0] &&
        'tags' in rule[0]
      ) {
        return this.authorizationSvc.userIsAndHasTag(rule as UserIsAndHasTag[]);
      }

      // If it's an array of role strings (common format), check user roles
      return this.authorizationSvc.userIs(rule as string[]);
    }

    // Handle the case where the rule is a map of projects to roles
    if (!(profile in rule)) {
      return true;
    }

    const projectRules = rule[profile];

    // Handle array-based project rules
    if (Array.isArray(projectRules)) {
      // If roles array is empty, allow access for all roles
      if (projectRules.length === 0) {
        return true;
      }

      // Check if it's roles with tags
      if (
        typeof projectRules[0] === 'object' &&
        'roles' in projectRules[0] &&
        'tags' in projectRules[0]
      ) {
        return this.authorizationSvc.userIsAndHasTag(projectRules as UserIsAndHasTag[]);
      }

      // Check if the user has one of the specified roles
      return this.authorizationSvc.userIs(projectRules as string[]);
    }

    // If it's not an array, treat as single value
    return this.authorizationSvc.userIs([projectRules]);
  }
}
